// src/js/cindy/live-timer-state.ts
//
// (a) P-09 / CINDY-04 / PITFALLS.md Pitfall 3 LOCK: `Date.now()` is the ONLY authoritative
//     elapsed-time source for this timer. `performance.now()` is explicitly FORBIDDEN here — it
//     is monotonic but is NOT guaranteed to keep advancing at the same rate, or advance at all,
//     while a page is suspended/frozen by the OS (mobile Safari/Chrome screen-lock). Every read
//     of "how much time has passed" must be `Date.now() - startInstant`, recomputed fresh on
//     demand — never an incrementing counter driven by `setInterval` ticks, which is the naive
//     trap PITFALLS.md documents in detail (screen-lock throttling, wall-clock vs. monotonic
//     confusion, and the missing-reconciliation-on-resume failure mode).
// (b) No value DERIVABLE from `startInstant` — the running duration, the countdown remainder —
//     is ever itself stored on `LiveTimerState` or persisted to the browser's storage. A cached
//     duration value is the incrementing-counter bug wearing a different disguise: it goes stale
//     the instant the tab is backgrounded and there is no mechanism to notice or correct it.
//     `startInstant` and the other boundary instants below are the only clock state that ever
//     gets stored; every displayed duration is recomputed from them on every read, including on
//     every `visibilitychange`/`pageshow` reconciliation, not only on the interval tick.
// (c) Deliberately zero-import so `tools/probe-264-timer-reconcile.mjs` can import this module
//     directly under Node's native TypeScript type stripping (Node >= 23.6; this repo runs v26)
//     — no browser, no bundler, no mocking. Same isolation rule `src/js/home/day-snap.ts`
//     (Phase 262) and `src/js/cindy/lap-parser.ts` (Phase 263) already established.

/** Per-round rep splits, when the operator is tracking sub-round movement breakdown. */
export interface MovementSplits {
    pullups: number;
    pushups: number;
    squats: number;
}

/** One completed round, captured at the instant the operator tapped "round complete". */
export interface CapturedRound {
    roundNo: number;
    durationSeconds: number;
    splits: MovementSplits | null;
}

export type LiveTimerStatus = 'pre-start' | 'running' | 'cap-reached' | 'finished';

/**
 * The full persistable/resumable state of a live Cindy timer session. Every field here is
 * either a wall-clock instant (a `Date.now()`-shaped millisecond value) or data captured at a
 * specific instant — never a value derived from "now" at write time. See docblock (b) above.
 */
export interface LiveTimerState {
    startInstant: number | null;
    roundBoundaryInstant: number | null;
    splitBoundaryInstant: number | null;
    pendingSplits: Partial<MovementSplits> | null;
    splitsMode: boolean;
    movementIndex: number;
    capturedRounds: CapturedRound[];
    partialReps: number;
    status: LiveTimerStatus;
}

/** Storage key. `ksat-` prefix follows `sidebar.ts`'s convention (src/js/sidebar.ts:3). */
export const STORAGE_KEY = 'ksat-cindy-live-timer';

/**
 * Cindy's prescribed movement order. Source of truth: P-07 /
 * `CindyModel::PULLUPS_PER_ROUND` / `PUSHUPS_PER_ROUND` / `SQUATS_PER_ROUND`
 * (app/Models/CindyModel.php:34-36) — pull-ups fill first, then push-ups, then squats.
 */
export const MOVEMENT_ORDER = ['pullups', 'pushups', 'squats'] as const;

/** UI-SPEC Copywriting Contract's exact display strings for each movement key. */
export const MOVEMENT_LABELS: Record<(typeof MOVEMENT_ORDER)[number], string> = {
    pullups: 'Pull-ups',
    pushups: 'Push-ups',
    squats: 'Squats',
};

/**
 * Seconds between a stored start instant and `now`, both `Date.now()`-shaped millisecond
 * values. Clamped at 0 so a backwards clock adjustment (or a corrupt persisted `startInstant`
 * that ended up in the future) can never produce a negative duration.
 */
export function computeElapsed(startInstant: number, now: number): number {
    return Math.max(0, (now - startInstant) / 1000);
}

/**
 * Whether the AMRAP cap has been reached. `>=`, so an exact `elapsedSeconds === capSeconds`
 * reads as reached. Must be evaluated on EVERY recompute (interval tick AND
 * visibilitychange/pageshow reconciliation), never only on the interval tick — a screen-lock
 * that outlasts the cap must read as capped the instant the screen wakes, not silently keep
 * counting past the cap until the next tick happens to land.
 */
export function isCapReached(durationSeconds: number, capSeconds: number): boolean {
    return durationSeconds >= capSeconds;
}

/**
 * Seconds remaining until the cap, clamped at 0 so a 40-minute screen-lock renders `00:00`,
 * never a negative countdown.
 */
export function computeRemaining(durationSeconds: number, capSeconds: number): number {
    return Math.max(0, capSeconds - durationSeconds);
}

/**
 * `MM:SS` formatter for the RUNNING timer display (UI-SPEC's running-state precision rule:
 * whole seconds only while running; `MM:SS.hh` below is reserved for captured laps). Floors
 * (never rounds) the seconds component so the running display never shows the cap value a
 * fraction of a second before the cap is actually reached.
 */
export function formatClock(totalSeconds: number): string {
    const clamped = Math.max(0, totalSeconds);
    const minutes = Math.floor(clamped / 60);
    const seconds = Math.floor(clamped - minutes * 60);
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/**
 * `MM:SS.hh` formatter for a captured lap/round duration — byte-identical to
 * `src/js/cindy/entry-form.ts`'s shipped private `formatDurationDisplay()` (lines ~77-84).
 * Deliberately duplicated rather than imported: `entry-form.ts` has imports of its own, and this
 * module must stay zero-import so `tools/probe-264-timer-reconcile.mjs` can load it directly
 * under Node's native TypeScript type stripping with no bundler. `tools/probe-264-timer-
 * reconcile.mjs`'s drift-guard case reads both files' bodies at probe time and fails loud the
 * instant they diverge — do NOT "fix" the known hundredths-carry rounding quirk here (e.g. a
 * value just under a whole second rounding its hundredths up to 100); any deviation from the
 * shipped copy is exactly the drift that guard exists to catch.
 */
export function formatDurationDisplay(totalSeconds: number): string {
    const clamped = Math.max(0, totalSeconds);
    const minutes = Math.floor(clamped / 60);
    const remainder = clamped - minutes * 60;
    const wholeSeconds = Math.floor(remainder);
    const hundredths = Math.round((remainder - wholeSeconds) * 100);
    return `${String(minutes).padStart(2, '0')}:${String(wholeSeconds).padStart(2, '0')}.${String(hundredths).padStart(2, '0')}`;
}

// -------------------------------------------------------------------------------------------
// Persistence. Every helper below takes the storage object as a PARAMETER rather than reaching
// for the browser's persistent-storage global directly — plain Node has no such global, so a
// helper that closes over it cannot be unit-probed at all, and this is the layer where a silent
// failure (private browsing, quota, corrupt JSON) would be LEAST visible. This is the one
// deliberate departure from 264-RESEARCH.md Pattern 5's sketch; keeping the module zero-global keeps it 100%
// probe-able under `tools/probe-264-timer-reconcile.mjs`. No ITP staleness threshold or
// automatic age-based clearing is implemented here — P-14 leaves that decision to the operator,
// and RESEARCH.md Pitfall B classifies a silently-evicted state as a self-healing degrade to the
// same "nothing to resume" branch `loadPersistedState()` already returns for any other failure.
// -------------------------------------------------------------------------------------------

/**
 * Storage abstraction the persistence helpers below take as a parameter. Satisfied by the real
 * the real browser storage global at call sites; `tools/probe-264-timer-reconcile.mjs` passes a tiny
 * in-memory fake so this module never has to touch a browser global to be proven correct.
 */
export interface StorageLike {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
    removeItem(key: string): void;
}

/** A fresh, never-started timer state. */
export function createInitialState(splitsMode: boolean): LiveTimerState {
    return {
        startInstant: null,
        roundBoundaryInstant: null,
        splitBoundaryInstant: null,
        pendingSplits: null,
        splitsMode,
        movementIndex: 0,
        capturedRounds: [],
        partialReps: 0,
        status: 'pre-start',
    };
}

const VALID_STATUSES: readonly LiveTimerStatus[] = ['pre-start', 'running', 'cap-reached', 'finished'];

/** `true` for a finite (non-NaN, non-Infinity) number. */
function isFiniteNumber(value: unknown): value is number {
    return typeof value === 'number' && Number.isFinite(value);
}

/** `true` for an instant field: `null`, or a finite number. */
function isValidInstant(value: unknown): value is number | null {
    return value === null || isFiniteNumber(value);
}

/**
 * Sanitizes one captured-round entry. Mirrors `CindyModel::saveSession()`'s server-side bounds
 * (app/Models/CindyModel.php — duration `0 < s <= 3600`, round-count ceiling 60; see T-264-01 in
 * this plan's threat register). Returns `null` if the entry fails any bound; failing entries are
 * DROPPED by the caller, never repaired.
 */
function sanitizeCapturedRound(raw: unknown): CapturedRound | null {
    if (raw === null || typeof raw !== 'object') return null;
    const r = raw as Record<string, unknown>;

    const roundNo = r.roundNo;
    if (!isFiniteNumber(roundNo) || !Number.isInteger(roundNo) || roundNo < 1) return null;

    const durationSeconds = r.durationSeconds;
    if (!isFiniteNumber(durationSeconds) || durationSeconds <= 0 || durationSeconds > 3600) return null;

    let splits: MovementSplits | null = null;
    if (r.splits !== null && r.splits !== undefined) {
        if (typeof r.splits !== 'object') return null;
        const s = r.splits as Record<string, unknown>;
        const pullups = s.pullups;
        const pushups = s.pushups;
        const squats = s.squats;
        if (
            !isFiniteNumber(pullups) || pullups < 0 ||
            !isFiniteNumber(pushups) || pushups < 0 ||
            !isFiniteNumber(squats) || squats < 0
        ) {
            return null;
        }
        splits = { pullups, pushups, squats };
    }

    return { roundNo, durationSeconds, splits };
}

/**
 * Total function: never throws, returns `null` for anything that is not a plausible timer
 * state — a non-object, `null`, a missing/unknown `status`, a non-array `capturedRounds`, or a
 * `startInstant` that is not a finite number or `null`. Every surviving field is coerced and
 * clamped: `capturedRounds` entries are validated by `sanitizeCapturedRound()` above and the
 * array is truncated to 60 entries (`saveSession()`'s own round-count ceiling); `partialReps` is
 * clamped to 0..29 (`REPS_PER_ROUND - 1`, matching the server). This is defence-in-depth for
 * T-264-01 (the persisted blob is fully user-editable) — the authoritative check remains
 * `CindyModel::saveSession()`'s existing server-side re-validation, which this function
 * deliberately mirrors rather than replaces.
 */
export function sanitizePersistedState(raw: unknown): LiveTimerState | null {
    if (raw === null || typeof raw !== 'object') return null;
    const r = raw as Record<string, unknown>;

    if (typeof r.status !== 'string' || !VALID_STATUSES.includes(r.status as LiveTimerStatus)) {
        return null;
    }
    if (!Array.isArray(r.capturedRounds)) return null;
    if (!isValidInstant(r.startInstant)) return null;

    const capturedRounds: CapturedRound[] = [];
    for (const entry of r.capturedRounds) {
        if (capturedRounds.length >= 60) break;
        const sanitized = sanitizeCapturedRound(entry);
        if (sanitized !== null) capturedRounds.push(sanitized);
    }

    const roundBoundaryInstant = isValidInstant(r.roundBoundaryInstant) ? r.roundBoundaryInstant : null;
    const splitBoundaryInstant = isValidInstant(r.splitBoundaryInstant) ? r.splitBoundaryInstant : null;

    let pendingSplits: Partial<MovementSplits> | null = null;
    if (r.pendingSplits !== null && r.pendingSplits !== undefined && typeof r.pendingSplits === 'object') {
        const p = r.pendingSplits as Record<string, unknown>;
        const coerced: Partial<MovementSplits> = {};
        for (const key of MOVEMENT_ORDER) {
            const v = p[key];
            if (isFiniteNumber(v) && v >= 0) coerced[key] = v;
        }
        pendingSplits = Object.keys(coerced).length > 0 ? coerced : null;
    }

    const partialRepsRaw = r.partialReps;
    const partialReps = isFiniteNumber(partialRepsRaw)
        ? Math.max(0, Math.min(29, Math.trunc(partialRepsRaw)))
        : 0;

    const movementIndexRaw = r.movementIndex;
    const movementIndex = isFiniteNumber(movementIndexRaw)
        ? Math.max(0, Math.min(MOVEMENT_ORDER.length - 1, Math.trunc(movementIndexRaw)))
        : 0;

    return {
        startInstant: r.startInstant as number | null,
        roundBoundaryInstant,
        splitBoundaryInstant,
        pendingSplits,
        splitsMode: Boolean(r.splitsMode),
        movementIndex,
        capturedRounds,
        partialReps,
        status: r.status as LiveTimerStatus,
    };
}

/**
 * Loads and sanitizes persisted timer state. Never throws — returns `null` for `null` storage,
 * an absent key, invalid JSON, or any shape `sanitizePersistedState()` rejects.
 */
export function loadPersistedState(storage: StorageLike | null): LiveTimerState | null {
    if (storage === null) return null;
    let raw: string | null;
    try {
        raw = storage.getItem(STORAGE_KEY);
    } catch {
        return null;
    }
    if (raw === null) return null;
    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch {
        return null;
    }
    return sanitizePersistedState(parsed);
}

/**
 * Persists the whole state as one atomic JSON blob under the single `STORAGE_KEY` — never spread
 * across multiple keys, and never including a derived running-duration or countdown-remainder
 * value (see docblock (b) at the top of this file). Returns `false` instead of throwing on
 * `QuotaExceededError` or Safari private-browsing's storage-disabled mode, so a failure can
 * never propagate into a tap handler mid-workout (T-264-05).
 */
export function persistState(storage: StorageLike | null, state: LiveTimerState): boolean {
    if (storage === null) return false;
    try {
        storage.setItem(STORAGE_KEY, JSON.stringify(state));
        return true;
    } catch {
        return false;
    }
}

/** Clears persisted timer state. Swallows the same failure modes as `persistState()` (T-264-05). */
export function clearPersistedState(storage: StorageLike | null): void {
    if (storage === null) return;
    try {
        storage.removeItem(STORAGE_KEY);
    } catch {
        // Swallow — a failed clear must never propagate into a tap handler (T-264-05).
    }
}

/**
 * Feature-detects whether `storage` is actually usable: a one-time write/read/delete round trip
 * against a throwaway key (`STORAGE_KEY + '-probe'`). Returns `false` on any throw or read-back
 * mismatch. This is what drives UI-SPEC's `.cindy-storage-warning` copy — it is a feature-detect,
 * never a speculative warning.
 */
export function isStorageAvailable(storage: StorageLike | null): boolean {
    if (storage === null) return false;
    const probeKey = STORAGE_KEY + '-probe';
    const probeValue = 'ok';
    try {
        storage.setItem(probeKey, probeValue);
        const readBack = storage.getItem(probeKey);
        storage.removeItem(probeKey);
        return readBack === probeValue;
    } catch {
        return false;
    }
}

// -------------------------------------------------------------------------------------------
// State-machine transitions (Plan 264-04). Every function below is PURE: it takes a state plus
// scalars and returns a NEW state object, never mutates its input (proven by
// `tools/probe-264-timer-reconcile.mjs`'s `no-mutation-of-input` case deep-freezing a state
// before calling every one of these), never touches a global, and never throws.
// -------------------------------------------------------------------------------------------

/** Leftover-reps ceiling — one fewer than a full round (`REPS_PER_ROUND`, 5+10+15=30),
 *  matching `CindyModel::saveSession()`'s own server-side clamp and `sanitizePersistedState()`
 *  above (T-264-01). */
const MAX_PARTIAL_REPS = 29;

/**
 * Starts the timer: `pre-start` -> `running`, stamping every boundary instant to `now`.
 * `splitsMode` (D-12: arrives already decided, defaults to one tap per round — see call sites'
 * `createInitialState()` argument) is carried through UNCHANGED and is never writable again
 * after this point (P-05: the toggle is a pre-start-only decision, locked for the session so a
 * saved session can never mix split and non-split rounds).
 */
export function startTimer(state: LiveTimerState, now: number): LiveTimerState {
    return {
        ...state,
        status: 'running',
        startInstant: now,
        roundBoundaryInstant: now,
        splitBoundaryInstant: now,
        movementIndex: 0,
        pendingSplits: null,
    };
}

/**
 * The single entry point for the one tap target, in both modes (P-06: the same button, the same
 * motor pattern, only the label differs). Returns `state` UNCHANGED (same reference — trivially
 * non-mutating) when `status !== 'running'` or `startInstant` is `null`: a tap that lands outside
 * a running session has nothing to capture.
 *
 * 1-tap mode: appends one `CapturedRound` per tap, `durationSeconds` the delta since the last
 * round boundary, `splits: null`.
 *
 * Splits mode: records the delta since the last split boundary into `pendingSplits` under
 * `MOVEMENT_ORDER[movementIndex]`, then advances `movementIndex`. Only once all three movements
 * have landed (`movementIndex` would reach 3) does a `CapturedRound` get appended — a partial
 * splits-round is never a history entry (263's P-08: `cindy_round` holds only complete rounds).
 *
 * Every derived duration is clamped at 0 (`Math.max(0, ...)`) so a backwards clock adjustment can
 * never produce a negative lap.
 */
export function captureTap(state: LiveTimerState, now: number): LiveTimerState {
    if (state.status !== 'running' || state.startInstant === null) {
        return state;
    }

    if (!state.splitsMode) {
        const durationSeconds = Math.max(0, (now - (state.roundBoundaryInstant ?? now)) / 1000);
        const round: CapturedRound = {
            roundNo: state.capturedRounds.length + 1,
            durationSeconds,
            splits: null,
        };
        return {
            ...state,
            capturedRounds: [...state.capturedRounds, round],
            roundBoundaryInstant: now,
            splitBoundaryInstant: now,
        };
    }

    // Splits mode: record this tap's delta under the CURRENT movement, then advance.
    const movementKey = MOVEMENT_ORDER[state.movementIndex];
    const splitSeconds = Math.max(0, (now - (state.splitBoundaryInstant ?? now)) / 1000);
    const pendingSplits: Partial<MovementSplits> = {
        ...(state.pendingSplits ?? {}),
        [movementKey]: splitSeconds,
    };
    const nextMovementIndex = state.movementIndex + 1;

    if (nextMovementIndex >= MOVEMENT_ORDER.length) {
        // All three movements landed — the round becomes a history entry NOW, not before.
        const durationSeconds = Math.max(0, (now - (state.roundBoundaryInstant ?? now)) / 1000);
        const splits: MovementSplits = {
            pullups: pendingSplits.pullups ?? 0,
            pushups: pendingSplits.pushups ?? 0,
            squats: pendingSplits.squats ?? 0,
        };
        const round: CapturedRound = {
            roundNo: state.capturedRounds.length + 1,
            durationSeconds,
            splits,
        };
        return {
            ...state,
            capturedRounds: [...state.capturedRounds, round],
            movementIndex: 0,
            pendingSplits: null,
            roundBoundaryInstant: now,
            splitBoundaryInstant: now,
        };
    }

    return {
        ...state,
        pendingSplits,
        movementIndex: nextMovementIndex,
        splitBoundaryInstant: now,
    };
}

/**
 * Removes exactly the most recent capture EVENT (UI-SPEC's resolved discretion item 4): a whole
 * round in 1-tap mode, a single movement split mid-round in splits mode, or the whole
 * just-completed round when splits mode is at a round boundary (`movementIndex === 0`, nothing
 * pending). Returns `state` UNCHANGED when there is nothing to undo.
 *
 * Deliberately does NOT store a stack of boundary instants — every boundary this function needs
 * is DERIVED from data already present on the state, which is both less state to keep in sync
 * and less state to persist:
 *   - after removing a round: `roundBoundaryInstant` = `startInstant` + 1000 x the sum of the
 *     REMAINING rounds' `durationSeconds` (and `splitBoundaryInstant` equals it — no round is
 *     ever mid-split immediately after a round-level undo);
 *   - after removing a split: `splitBoundaryInstant` = `roundBoundaryInstant` + 1000 x the sum
 *     of the splits still pending, and `movementIndex` decrements.
 * This identity holds because every captured/pending duration IS the wall-clock delta between
 * consecutive boundaries by construction (`captureTap()` above) — summing them back up from a
 * known earlier boundary reconstructs the later one exactly, with no separate ledger required.
 */
export function undoLast(state: LiveTimerState): LiveTimerState {
    if (state.splitsMode && state.movementIndex > 0) {
        const removedKey = MOVEMENT_ORDER[state.movementIndex - 1];
        const remainingPending: Partial<MovementSplits> = { ...(state.pendingSplits ?? {}) };
        delete remainingPending[removedKey];
        const pendingSplits = Object.keys(remainingPending).length > 0 ? remainingPending : null;

        const pendingSum = Object.values(remainingPending).reduce((sum, v) => sum + (v ?? 0), 0);
        const roundBoundaryInstant = state.roundBoundaryInstant ?? state.startInstant ?? 0;
        const splitBoundaryInstant = roundBoundaryInstant + pendingSum * 1000;

        return {
            ...state,
            movementIndex: state.movementIndex - 1,
            pendingSplits,
            splitBoundaryInstant,
        };
    }

    if (state.capturedRounds.length > 0) {
        const remainingRounds = state.capturedRounds.slice(0, -1);
        const remainingSum = remainingRounds.reduce((sum, r) => sum + r.durationSeconds, 0);
        const startInstant = state.startInstant ?? 0;
        const roundBoundaryInstant = startInstant + remainingSum * 1000;

        return {
            ...state,
            capturedRounds: remainingRounds,
            roundBoundaryInstant,
            splitBoundaryInstant: roundBoundaryInstant,
            movementIndex: 0,
            pendingSplits: null,
        };
    }

    return state;
}

/**
 * `running`/`cap-reached` -> `cap-reached`, discarding whatever round was in progress. This is
 * P-12 plus 263's P-08: the time spent inside the unfinished round is deliberately not stored
 * anywhere on the returned state — the leftover-reps field (set later, via `finishSession()`) is
 * how that work gets accounted for instead. `capturedRounds` is untouched; only the in-progress
 * partial (`pendingSplits`/`movementIndex`) is discarded.
 */
export function markCapReached(state: LiveTimerState): LiveTimerState {
    return {
        ...state,
        status: 'cap-reached',
        pendingSplits: null,
        movementIndex: 0,
    };
}

/**
 * -> `finished`. Same in-progress-round discard as `markCapReached()` — a manual finish mid-round
 * loses that round's partial time by the same rule, not a different one. `partialReps` is coerced
 * to an integer (accepting a numeric-string input, `Number(partialReps)`, the same tolerance
 * `CindyModel::saveSession()`'s own parsing affords) and clamped to `0..29`
 * (`MAX_PARTIAL_REPS` = `REPS_PER_ROUND - 1`, the same ceiling the server enforces).
 */
export function finishSession(state: LiveTimerState, partialReps: number): LiveTimerState {
    const numeric = Number(partialReps);
    const coerced = Number.isFinite(numeric) ? Math.trunc(numeric) : 0;
    const clamped = Math.max(0, Math.min(MAX_PARTIAL_REPS, coerced));
    return {
        ...state,
        status: 'finished',
        partialReps: clamped,
        pendingSplits: null,
        movementIndex: 0,
    };
}

/**
 * `true` only for `status` of `'running'` or `'cap-reached'`. This is what decides whether P-14's
 * resume/discard prompt appears at all: a `'finished'` or `'pre-start'` persisted state has
 * nothing to offer — there is no in-progress work to resume.
 */
export function isResumable(state: LiveTimerState): boolean {
    return state.status === 'running' || state.status === 'cap-reached';
}

// -------------------------------------------------------------------------------------------
// UI descriptors and the hand-off mapper (Plan 264-04). These stay in this zero-import module
// (rather than the future DOM module) specifically so their copy can be asserted by the plain-
// Node probe — every string below is a Copywriting Contract row from 264-UI-SPEC.md.
// -------------------------------------------------------------------------------------------

/**
 * Returns the primary tap target's label plus the round sub-label. In 1-tap mode `label` is
 * always the static `'Complete Round'`. In splits mode it cycles
 * `MOVEMENT_LABELS[MOVEMENT_ORDER[movementIndex]]` — Pull-ups then Push-ups then Squats (P-06/
 * P-07). `roundLabel` is `'Round ' + (capturedRounds.length + 1)` in BOTH modes — 1-based, and in
 * splits mode it does NOT increment until all three movements for the current round have landed
 * (a round only becomes a `capturedRounds` entry once complete — see `captureTap()` above).
 */
export function describeTapTarget(state: LiveTimerState): { label: string; roundLabel: string } {
    const roundLabel = 'Round ' + (state.capturedRounds.length + 1);
    if (!state.splitsMode) {
        return { label: 'Complete Round', roundLabel };
    }
    return { label: MOVEMENT_LABELS[MOVEMENT_ORDER[state.movementIndex]], roundLabel };
}

/**
 * Returns the Undo control's label, disabled state and kind, covering UI-SPEC's three variants
 * exactly. The label always names what will be removed — a bare `'Undo'` is only ever emitted
 * for the disabled case (nothing captured, nothing pending): the control renders disabled rather
 * than hidden so its position never moves during a session.
 */
export function describeUndoTarget(
    state: LiveTimerState
): { label: string; disabled: boolean; kind: 'round' | 'split' | 'none' } {
    if (state.capturedRounds.length === 0 && state.movementIndex === 0) {
        return { label: 'Undo', disabled: true, kind: 'none' };
    }
    if (state.splitsMode && state.movementIndex > 0) {
        const movementKey = MOVEMENT_ORDER[state.movementIndex - 1];
        return { label: 'Undo ' + MOVEMENT_LABELS[movementKey], disabled: false, kind: 'split' };
    }
    return { label: 'Undo Round ' + state.capturedRounds.length, disabled: false, kind: 'round' };
}

/**
 * Resolves a `Date.now()`-shaped instant to a `YYYY-MM-DD` CALENDAR-DATE string in `timeZone`
 * (call sites pass `'America/Toronto'`) via `toLocaleDateString('en-CA', { timeZone })` — never
 * by slicing an ISO string, which would silently read the UTC calendar date instead. This value
 * must never be run back through any timezone conversion downstream: the off-by-one that pattern
 * causes is this project's highest-priority standing constraint (STATE.md § "Standing
 * Constraints" — Timezone; a confirmed production incident).
 */
export function toLocalYmd(nowMs: number, timeZone: string): string {
    return new Date(nowMs).toLocaleDateString('en-CA', { timeZone });
}

/**
 * Formats the P-14 resume prompt's `{Mon D, Year}` display string (e.g. `Aug 6, 2026`) via
 * `toLocaleDateString('en-US', { timeZone, month: 'short', day: 'numeric', year: 'numeric' })`.
 * Display only — never parsed back into data.
 */
export function formatResumeDate(startInstantMs: number, timeZone: string): string {
    return new Date(startInstantMs).toLocaleDateString('en-US', {
        timeZone,
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

/**
 * Maps captured timer state into the exact object `mountCindyEntry()`'s `refresh()` accepts
 * (`src/js/cindy/entry-form.ts`'s `CindyEntrySession`) — `id: null` always (a create-mode mount;
 * the live page has no Delete button and never edits an existing session), `note: null`, and a
 * `rounds` array of `{ round_no, duration_display, pullup_split, pushup_split, squat_split }` in
 * FORWARD `round_no` order 1..N (the newest-first ordering some future round-history display may
 * use is a display-only concern and must never leak into this hand-off payload — `capturedRounds`
 * is sorted defensively here, though it is already forward-ordered by construction). The three
 * split fields are the captured numbers when that round has a triple and explicit `null`
 * otherwise, matching the index-aligned, all-three-or-none contract
 * `CindyModel::saveSession()` enforces server-side (264-03-SUMMARY.md).
 */
export function toEntrySession(
    state: LiveTimerState,
    dateYmd: string
): {
    id: number | null;
    date: string;
    rounds_completed: number;
    partial_reps: number;
    note: string | null;
    rounds: {
        round_no: number;
        duration_display: string;
        pullup_split: number | null;
        pushup_split: number | null;
        squat_split: number | null;
    }[];
} {
    const rounds = state.capturedRounds
        .slice()
        .sort((a, b) => a.roundNo - b.roundNo)
        .map((round) => ({
            round_no: round.roundNo,
            duration_display: formatDurationDisplay(round.durationSeconds),
            pullup_split: round.splits ? round.splits.pullups : null,
            pushup_split: round.splits ? round.splits.pushups : null,
            squat_split: round.splits ? round.splits.squats : null,
        }));

    return {
        id: null,
        date: dateYmd,
        rounds_completed: state.capturedRounds.length,
        partial_reps: state.partialReps,
        note: null,
        rounds,
    };
}
